import { useState } from 'react'
import { flushSync } from 'react-dom'
import { DateRangePicker, defaultStaticRanges, defaultInputRanges, createStaticRanges } from 'react-date-range'
import { addDays, endOfWeek, format, startOfToday, startOfWeek } from 'date-fns'
import { Dropdown, Spinner } from 'react-bootstrap'
import { Range, RangeKeyDict } from 'react-date-range'
import { enGB } from 'date-fns/locale'
import { useAppContext } from '../../../../AppContext'

export default function DateRangeSelector(opts: {
    onSubmit: (startDate: Date, endDate: Date) => Promise<void>
}) {
    const appContext = useAppContext()

    const getFromAndToDefault = () => {
        if(appContext.isPdf && appContext.fromDate && appContext.toDate) {
            return {fromDate: new Date(appContext.fromDate), toDate: new Date(appContext.toDate)}
        }


        return {fromDate: addDays(new Date(), -30), toDate: new Date()}
    }
    const { fromDate, toDate } = getFromAndToDefault()
    const today = startOfToday()

    const [chosenRange, setChosenRange] = useState<Range[]>([{
        startDate: fromDate,
        endDate: toDate,
        key: 'selection'
    }])

    const [tempRange, setTempRange] = useState<Range[]>([{
        // startDate: addDays(new Date(), -30),
        // endDate: new Date(),
        startDate: fromDate,
        endDate: toDate,
        key: 'selection'
    }])

    const [isOpen, setIsOpen] = useState<boolean>(false)
    const [isLoading, setIsLoading] = useState<boolean>(false)

    const mondayWeekStart = { weekStartsOn: 1 as 1, locale: enGB }

    const remapStaticRange = (rangeObj: any) => {
        return {
            ...rangeObj,
            range: () => {
                const r = rangeObj.range()

                if (rangeObj.label.toLowerCase().includes('week')) {
                    const baseDate = rangeObj.label.toLowerCase().includes('last')
                        ? addDays(new Date(), -7)
                        : new Date()

                    return {
                        startDate: startOfWeek(baseDate, mondayWeekStart),
                        endDate: endOfWeek(baseDate, mondayWeekStart),
                        key: r.key || 'selection'
                    }
                }

                return r
            }
        }
    }

    const mondayStaticRanges = defaultStaticRanges.map(remapStaticRange)

    const customRanges = [30, 60, 90].map(num => ({
        label: `${num} Days`,
        range: () => ({
            startDate: addDays(new Date(), -num),
            endDate: new Date(),
            key: 'selection'
        })
    }))

    const customStaticRanges = createStaticRanges([
        ...mondayStaticRanges,
        ...customRanges
    ])


    const customInputRanges = defaultInputRanges.filter(
        (range: any) => !['days up to today', 'days starting today'].includes(range.label)
    )

    const handleSelect = (ranges: RangeKeyDict) => {
        setTempRange([ranges.selection])
    }

    const orientation: 'horizontal' | 'vertical' = window.matchMedia("(max-width: 700px)").matches ? 'vertical' : 'horizontal'

    const toggleDropdown = () => {
        setIsOpen(!isOpen)
    }

    const formatDateRange = (startDate?: Date, endDate?: Date): string => {
        if (!startDate || !endDate) return ''
        return `${format(startDate, 'MMM dd, yyyy')} - ${format(endDate, 'MMM dd, yyyy')}`
    }

    const onSubmit = async () => {
        flushSync(() => {
            setIsLoading(true)
        })

        await new Promise<void>((resolve) => {
            requestAnimationFrame(() => resolve())
        })

        const startDate0 = tempRange[0].startDate
        const endDate0 = tempRange[0].endDate
        if(startDate0 && endDate0) {
            const startDate = new Date(startDate0)
            startDate.setHours(0,0,0,0)
            const endDate = new Date(endDate0)
            endDate.setHours(0,0,0,0)

            await opts.onSubmit(startDate, endDate)
            setChosenRange(tempRange)            
        }
        

        setIsLoading(false) 
        setIsOpen(false) 
    }

    return (
        <Dropdown show={isOpen} onToggle={toggleDropdown}>
            <Dropdown.Toggle 
                className="btn-sm btn-light dropdown-as-text"
            >
                {formatDateRange(chosenRange[0].startDate, chosenRange[0].endDate)}
            </Dropdown.Toggle>

            <Dropdown.Menu show={isOpen} align="end" className="p-3" style={{ minWidth: '350px' }}>
                <DateRangePicker
                    onChange={handleSelect}
                    moveRangeOnFirstSelection={false}
                    months={2}
                    ranges={tempRange}
                    staticRanges={customStaticRanges}
                    inputRanges={customInputRanges}
                    direction={orientation}
                    maxDate={today}
                    locale={enGB}
                />
                <div className="mt-3 d-flex justify-content-end">
                    <button 
                        className="btn btn-primary btn-sm" 
                        onClick={onSubmit} 
                        disabled={isLoading}
                    >
                        {isLoading ? <Spinner as="span" animation="border" size="sm" /> : 'Submit'} 
                    </button>
                </div>
            </Dropdown.Menu>
        </Dropdown>
    )
}
